import { useMemo } from "react";
import { haversineKm } from "./shared";

/**
 * Computes the polylines + ETA breakdown to render whenever the admin
 * clicks a pickup or dropoff pin on the map. Returns `null` for any other
 * selection (driver pins, nothing selected, ride without coords).
 *
 * Output shape:
 *   {
 *     routes: [{points, color, style, weight, opacity}, ...],
 *     tripKm, tripMin,
 *     driverToPickupKm, driverToPickupMin,  // null when unassigned
 *     assignedDriver                        // the driver doc, or null
 *   }
 */
export function useRoutePreview(selectedMarker, drivers) {
  return useMemo(() => {
    if (!selectedMarker || !["pickup", "dropoff"].includes(selectedMarker.kind)) return null;
    const ride = selectedMarker.payload;
    if (!ride || ride.pickup_lat == null || ride.dropoff_lat == null) return null;

    const routes = [];
    const pickupPt = { lat: ride.pickup_lat, lng: ride.pickup_lng };
    const dropoffPt = { lat: ride.dropoff_lat, lng: ride.dropoff_lng };
    // Main trip leg — solid black, prominent
    routes.push({ points: [pickupPt, dropoffPt], color: "#0A0A0A", weight: 4, opacity: 0.85 });

    const assignedDriverId = ride.driver_id || ride.offered_to;
    const assignedDriver = assignedDriverId
      ? drivers.find((d) => d.id === assignedDriverId)
      : null;
    let driverToPickupKm = null;
    if (assignedDriver && assignedDriver.current_lat != null && assignedDriver.current_lng != null) {
      const driverPt = { lat: assignedDriver.current_lat, lng: assignedDriver.current_lng };
      // Pre-pickup leg — dashed yellow
      routes.push({ points: [driverPt, pickupPt], color: "#FFC107", weight: 4, style: "dashed", opacity: 1 });
      driverToPickupKm = haversineKm(driverPt.lat, driverPt.lng, pickupPt.lat, pickupPt.lng);
    }
    const tripKm = haversineKm(pickupPt.lat, pickupPt.lng, dropoffPt.lat, dropoffPt.lng);
    return {
      routes,
      tripKm,
      tripMin: ride.estimated_duration_min || (tripKm / 30) * 60,
      driverToPickupKm,
      driverToPickupMin: driverToPickupKm != null ? (driverToPickupKm / 30) * 60 : null,
      assignedDriver,
    };
  }, [selectedMarker, drivers]);
}
